import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from '../services/auth.service';

/**
 * Two responsibilities:
 *   1. If MSAL has no active account, kick off a redirect login.
 *   2. Once signed in, ensure /api/auth/me has been loaded so guards/components
 *      downstream can read AuthService.me() synchronously.
 */
export const authGuard: CanActivateFn = async () => {
  const msal = inject(MsalService);
  const auth = inject(AuthService);
  const router = inject(Router);

  // MSAL Browser v3 requires explicit initialization before any instance
  // method (loginRedirect, getAllAccounts, etc.) — calling it on every guard
  // hit is safe; the library short-circuits after the first successful init.
  await firstValueFrom(msal.initialize());

  const account = auth.getActiveAccount();
  if (!account) {
    await msal.instance.loginRedirect({
      scopes: environment.auth.apiScopes,
    });
    // Redirect aborts navigation; returning false keeps the router idle until
    // the redirect cycle completes and the guard re-runs.
    return false;
  }

  const me = await auth.loadMe();
  if (!me) {
    return false;
  }

  if (!auth.hasAnyAccess()) {
    return router.parseUrl('/no-access');
  }

  return true;
};
